import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const IDLE_TIMEOUT = 60000; // 1 minute
const COUNTDOWN_DURATION = 60; // 60 seconds

interface IdleTimeoutDialogProps {
  onClose: () => void;
  onTimeout: () => void;
}

export function IdleTimeoutDialog({
  onClose,
  onTimeout,
}: IdleTimeoutDialogProps) {
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetIdleTimeout = () => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    idleTimeoutRef.current = setTimeout(() => {
      setShowCountdown(true);
      startCountdown();
    }, IDLE_TIMEOUT);
  };

  const startCountdown = () => {
    setCountdown(COUNTDOWN_DURATION);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setShowCountdown(false);
    resetIdleTimeout();
    onClose();
  };

  useEffect(() => {
    resetIdleTimeout();

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
    ];

    const handleUserActivity = () => {
      if (!showCountdown) {
        resetIdleTimeout();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [showCountdown]);

  return (
    <Dialog
      open={showCountdown}
      onOpenChange={(open) => !open && cancelCountdown()}
    >
      <DialogContent className="">
        <DialogHeader>
          <DialogTitle>Session Timeout Warning</DialogTitle>
          <DialogDescription>
            Due to inactivity, this window will close in {countdown} seconds.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0">
          <Button variant="outline" onClick={cancelCountdown}>
            Keep Window Open
          </Button>
          <Button variant="destructive" onClick={onTimeout}>
            Close Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
